import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/lib/apiClient';

interface LeadRow {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  companyName: string | null;
  status: string;
  score: number | null;
  createdAt: string;
}

interface HandoffRow {
  id: string;
  leadId: string;
  leadFirstName: string | null;
  leadLastName: string | null;
  leadEmail: string | null;
  state: string;
  handoffNote: string | null;
  createdAt: string;
}

function leadName(l: Pick<LeadRow, 'firstName' | 'lastName' | 'email' | 'id'>) {
  return [l.firstName, l.lastName].filter(Boolean).join(' ') || l.email || l.id.slice(0, 8);
}

export function SdrWorkspacePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [leads, setLeads] = useState<LeadRow[]>([]);
  const [handoffs, setHandoffs] = useState<HandoffRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [leadsRes, handoffsRes] = await Promise.all([
        apiClient.get<LeadRow[]>('/leads', { params: { limit: 20 } }),
        apiClient.get<HandoffRow[]>('/lead-handoffs/mine'),
      ]);
      setLeads(leadsRes.data);
      setHandoffs(handoffsRes.data);
    } catch {
      setLeads([]);
      setHandoffs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Calling queue: leads with status QUALIFIED sorted by score desc
  const callingQueue = leads
    .filter((l) => l.status === 'QUALIFIED')
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0));

  const pendingHandoffs = handoffs.filter((h) => h.state === 'PENDING');

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <Breadcrumbs crumbs={[{ label: t('workspace.sdr_title') }]} />
      <h1 className="text-2xl font-semibold mt-2">{t('workspace.sdr_title')}</h1>
      <p className="text-muted-foreground text-sm mt-1">{t('workspace.sdr_subtitle')}</p>

      {loading ? (
        <p className="text-muted-foreground mt-6 text-sm">{t('common.loading')}</p>
      ) : (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* My leads */}
          <div className="border rounded-xl p-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
              {t('workspace.leads_inbox')}
            </h2>
            {leads.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('workspace.empty_leads')}</p>
            ) : (
              <div className="space-y-2">
                {leads.slice(0, 10).map((l) => (
                  <button
                    key={l.id}
                    onClick={() => navigate(`/leads/${l.id}`)}
                    className="w-full text-left flex items-center justify-between px-3 py-2 rounded-lg hover:bg-muted transition-colors text-sm"
                  >
                    <div>
                      <span className="font-medium">{leadName(l)}</span>
                      {l.companyName && <span className="text-muted-foreground ml-2 text-xs">{l.companyName}</span>}
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      l.status === 'QUALIFIED' ? 'bg-emerald-900/40 text-emerald-200' :
                      l.status === 'NEW' ? 'bg-blue-900/40 text-blue-200' :
                      'bg-muted text-foreground'
                    }`}>
                      {l.status}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Calling queue */}
          <div className="border rounded-xl p-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
              {t('workspace.calling_queue')}
            </h2>
            {callingQueue.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('workspace.empty_leads')}</p>
            ) : (
              <div className="space-y-2">
                {callingQueue.slice(0, 8).map((l, idx) => (
                  <button
                    key={l.id}
                    onClick={() => navigate(`/leads/${l.id}`)}
                    className="w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted transition-colors text-sm"
                  >
                    <span className="shrink-0 w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-semibold text-muted-foreground">
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <span className="font-medium">{leadName(l)}</span>
                      {l.companyName && <span className="text-muted-foreground ml-2 text-xs">{l.companyName}</span>}
                    </div>
                    {l.score != null && (
                      <span className="text-xs font-mono text-muted-foreground">{l.score}</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Pending handoffs */}
          <div className="border rounded-xl p-4 md:col-span-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
              {t('workspace.handoff_drafts')}
            </h2>
            {pendingHandoffs.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('workspace.empty_handoffs')}</p>
            ) : (
              <div className="space-y-2">
                {pendingHandoffs.map((h) => (
                  <div key={h.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/30 text-sm">
                    <div>
                      <span className="font-medium">
                        {[h.leadFirstName, h.leadLastName].filter(Boolean).join(' ') || h.leadEmail || h.leadId.slice(0, 8)}
                      </span>
                      {h.handoffNote && (
                        <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-md">{h.handoffNote}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-amber-900/40 text-amber-200 px-2 py-0.5 rounded-full">{h.state}</span>
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/leads/${h.leadId}`)}>
                        {t('common.view')}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
